import BayunError from "../constants/BayunError";
import LmsErrorConstants from "../constants/LmsErrorConstants";
import SharedPreferences from "../constants/SharedPreferences";
import {
  aeadEncryptWithStorageKey,
  aeadDecryptWithStorageKey,
} from "../crypto/AuthenticatedEncryption";
import SigningPublicKeyTags from "../constants/SigningPublicKeyTags";
import { split } from "shamir";
import {
  EncryptionMode,
  EncryptionPolicy,
  KeyGenerationPolicy,
  LocalDataEncryptionMode,
} from "../constants/Enums";
import { getAuthRestclient } from "../rest/RestUtility";
import { join } from "shamir";
import Constants from "../constants/Constants";
import {
  hmacSha256Raw,
  generateHMacHashEncoded,
} from "../crypto/KeyMaterial";
import { encodeKey } from "../crypto/PBKDF2";
import { STATE_KEY, setActiveAppId } from "./StorageKeys";
import {
  clearSessionScope,
  readSessionValue,
  writeSessionValue,
} from "./SessionStorage";
import {
  enforceSessionCap,
  markSignedIn,
  reapAbandonedSessions,
  sessionsOverCap,
  getSessionMode,
  getSessionRecord,
  listAllSessions,
  listSessions,
  removeSessionRecord,
  upsertSessionRecord,
} from "./SessionRegistry";
import { reportReauthenticationNeeded } from "./Reauthentication";
import {
  clearActiveSessionId,
  getActiveSessionId as readActiveSessionId,
  setActiveSessionId,
} from "./ActiveSession";
import { SESSION_EVENTS, publishSessionEvent } from "./SessionEvents";
import {
  clearContext,
  getInternalStorageMap,
  getInternalStorageValue,
  getStorageKeyFor,
  setInternalStorageValue,
  setStorageKeyFor,
} from "./SessionContext";

export { setActiveAppId };

const ENCRYPTION_TYPE_RSA_AES = "RSA-AES";
const ENCRYPTION_TYPE_ECC = "ECC";

const LOCAL_ENCRYPTION_VERIFICATION_STRING =
  "LocalEncryptionVerificationString";

var crypto = require("crypto");

/**
 * This list contains the items that have to be saved encrypted in local storage.
 */
const itemsToBeEncrypted = [
  SharedPreferences.MEMBER_APP_PRIVATE_KEY,
  SharedPreferences.APP_SECRET,
  SharedPreferences.ACCESS_APP_PRIVATE_KEY,
  SharedPreferences.MEMBER_CREATION_APP_PRIVATE_KEY,
  SharedPreferences.ORG_CREATION_APP_PRIVATE_KEY,
  SharedPreferences.AUTHORISATION_APP_PRIVATE_KEY,
  SharedPreferences.MEMBER_PRIVATE_KEY,
  SharedPreferences.ORG_KEY,
  SharedPreferences.MEMBER_KEY,
  SharedPreferences.JOINED_GROUP_KEYS,
  SharedPreferences.ORG_SIGNING_PRIVATE_KEY,
  SharedPreferences.MEMBER_SIGNING_PRIVATE_KEY,
  SharedPreferences.DECRYPTED_CHALLENGE,
  SharedPreferences.CHAIN_KGP_OBJECTS_LIST,
  SharedPreferences.LAST_CHAIN_OBJECT_FOR + "1",
  SharedPreferences.LAST_CHAIN_OBJECT_FOR + "3",
  SharedPreferences.LAST_CHAIN_OBJECT_FOR + "4",
  SharedPreferences.LAST_CHAIN_OBJECT_FOR + "5",
  SharedPreferences.STATISTICS_KEY,
  SharedPreferences.USER_PRIVATE_KEY,
  SharedPreferences.ADMIN_PRIVATE_KEY,
  SharedPreferences.ADMIN_SIGNING_PRIVATE_KEY,
  // The backdoor signing key attests security-admin member public keys, so it must be
  // encrypted at rest like every other signing private key above. It was previously written
  // in plaintext because it is saved next to the backdoor *public* keys.
  SharedPreferences.BACKDOOR_SIGNING_PRIVATE_KEY,
  SharedPreferences.USER_SIGNING_PRIVATE_KEY,
  SharedPreferences.MEMBER_APP_SIGNING_PRIVATE_KEY,
  SharedPreferences.ACCESS_APP_SIGNING_PRIVATE_KEY,
  SharedPreferences.MEMBER_CREATION_APP_SIGNING_PRIVATE_KEY,
  SharedPreferences.ORG_CREATION_APP_SIGNING_PRIVATE_KEY,
  SharedPreferences.AUTHORISATION_APP_SIGNING_PRIVATE_KEY,
  SharedPreferences.DEVELOPER_KEY,
  // We are not using any where as of now might update in future.
  SharedPreferences.BACKDOOR_PRIVATE_KEY_PART_KEK,
];

/**
 * This function converts the given string to an array buffer.
 *
 * @param {String} str The string to be converted.
 * @returns {ArrayBuffer} The string as an array buffer.
 */
export const convertStringToArrayBuffer = (str) => {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
};

/**
 * This function converts the array buffer into a string.
 *
 * @param {ArrayBuffer} buffer The array buffer to be converted.
 * @returns {String} The array buffer as a string.
 */
export const convertArrayBufferToString = (buffer) => {
  // return String.fromCharCode.apply(null, new Uint8Array(buffer));
  return Uint8ToString(new Uint8Array(buffer));
};

// Divide in Chunk to ignore error stackOverFlow for big strings
function Uint8ToString(u8a) {
  var CHUNK_SZ = 0x8000;
  var c = [];
  for (var i = 0; i < u8a.length; i += CHUNK_SZ) {
    c.push(String.fromCharCode.apply(null, u8a.subarray(i, i + CHUNK_SZ)));
  }
  return c.join("");
}

/**
 * This function creates a random IV.
 * @returns {Uint8Array} the created IV.
 */
export const getRandomIv = () => {
  return window.crypto.getRandomValues(new Uint8Array(12));
};

/**
 * This function converts the arrayBuffer into a base64 string.
 *
 * @param {ArrayBuffer} arrayBuffer The array buffer to be converted.
 * @returns {String} The arrayBuffer as a base64 string.
 */
export const convertArrayBufferToBase64String = (arrayBuffer) => {
  const text = convertArrayBufferToString(arrayBuffer);
  return window.btoa(text);
};

/**
 * This function converts the base64 string into an array buffer.
 *
 * @param {String} base64String The string to be converted.
 * @returns {ArrayBuffer} The string as an array buffer.
 */
export const convertBase64StringToArrayBuffer = (base64String) => {
  const text = window.atob(base64String);
  return convertStringToArrayBuffer(text);
};

/**
 * Creates a new Uint8Array based on two different ArrayBuffers
 *
 * @param {ArrayBuffer} buffer1 The first buffer.
 * @param {ArrayBuffer} buffer2 The second buffer.
 * @return {ArrayBuffer} The new ArrayBuffer created out of the two.
 */
export const concatArrayBuffers = (buffer1, buffer2) => {
  const tmp = new Uint8Array(buffer1.byteLength + buffer2.byteLength);
  tmp.set(new Uint8Array(buffer1), 0);
  tmp.set(new Uint8Array(buffer2), buffer1.byteLength);

  return tmp.buffer;
};

/**
 * Generates a random salt: 32 random bytes encoded as standard Base64.
 * Matches Java Util.generateSalt() so PBKDF2 can Base64-decode the salt string.
 * (Name retained for call-site compatibility; not bcrypt.)
 *
 * @returns {String} Base64-encoded 32-byte salt
 */
export const generateBCryptSalt = () => {
  return generateSalt();
};

/**
 * Generates a random salt: 32 random bytes encoded as standard Base64.
 * Matches Java Util.generateSalt().
 *
 * @returns {String} Base64-encoded 32-byte salt
 */
export const generateSalt = () => {
  const saltBytes = new Uint8Array(32);
  (globalThis.crypto || window.crypto).getRandomValues(saltBytes);
  return convertArrayBufferToBase64String(saltBytes.buffer);
};

/**
 * This function generates a bcrypt hash for given string and salt.
 *
 * @param {String} string The text on which the hash is to be produced.
 * @param {String} salt The salt used to create the hash.
 * @returns {String} The generated hash.
 */
export const bayunHash = async (string, salt) => {
  // NOTE: this helper is currently unused and depends on a `bcrypt` library
  // that is not bundled with the SDK. Throw explicitly instead of failing with
  // a confusing ReferenceError if it is ever called.
  throw new Error("bayunHash is not implemented: bcrypt dependency unavailable");
};

/**
 * @deprecated Prefer hmacSha256Raw for AES keys, or encodeKey(hmacSha256Raw(...)) for wire.
 * Returns full Base64 of 32-byte HMAC-SHA256 (no truncation) — matches Java generateHMACKey.
 *
 * @param {Uint8Array|ArrayBuffer|string} key
 * @param {Uint8Array|ArrayBuffer|string} message
 * @returns {Promise<string>}
 */
export const generateHMacHash = async (key, message) => {
  return generateHMacHashEncoded(key, message);
};

/**
 * Create a random key (matches Android SecKeyManager.generateRandomKey).
 * Returns full Base64 of HMAC digest (no truncation) for transport/SSS payloads.
 *
 * @returns {Promise<String>} randomly generated key (Base64 of 32 raw bytes)
 */
export const generateRandomKey = async () => {
  const randomKeySalt = generateBCryptSalt();
  const randomKeyName = "RandomKey";
  const timestamp = new Date().toString();
  const compositeString = randomKeyName + timestamp + randomKeySalt;
  return encodeKey(await hmacSha256Raw(compositeString, randomKeySalt));
};

/**
 * Generates SHA-256 hash of provided text
 * Cross-platform compatible with Java implementation
 *
 * @param {String} plainText The text to hash
 * @returns {String} Hexadecimal encoded SHA-256 hash
 */
export const generateSha256Hash = async (plainText) => {
  if (!plainText) {
    return null;
  }
  // Use TextEncoder for proper UTF-8 encoding to match Java implementation
  const encoder = new TextEncoder();
  const msgBuffer = encoder.encode(plainText);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", msgBuffer);

  // Convert ArrayBuffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hexString = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  return hexString;
};

/**
 * This method returns a randomly generated version 4 uuid.
 */
export const generateRandomUuid = () => {
  let dt = new Date().getTime();
  const uuid = "xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = ((dt + Math.random() * 16) % 16) | 0;
    dt = Math.floor(dt / 16);
    return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
};

/**
 * Reads a value staged in memory for a session, before the storage key is available to encrypt it.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} identifier the id of the staged value.
 */
export const getFromInternalStorage = async (sessionId, identifier) =>
  getInternalStorageValue(sessionId, identifier);

/**
 * Stages a value in memory for a session until the storage key arrives and it can be encrypted.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} identifier the id of the value to stage.
 * @param {String} value the value to stage.
 */
export const saveInInternalStorage = async (sessionId, identifier, value) => {
  setInternalStorageValue(sessionId, identifier, value);
};

/**
 * Fetches the data from local storage from the state saved against the sessionId.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} identifier the id of the value to be picked from local storage.
 */
export const getFromStorage = async (sessionId, identifier) => {
  var stateString = getStoredState(sessionId);

  if (!stateString) {
    console.error("No stored data found for this session");
    // Tell the host before throwing. Callers all over the SDK and its apps catch this and log it,
    // which left an expired session looking like a hung page rather than a sign-out.
    reportReauthenticationNeeded(sessionId);
    throw BayunError.ERROR_REAUTHENTICATION_NEEDED;
  }

  const state = new Map(JSON.parse(stateString));

  const isEncrypted = itemsToBeEncrypted.includes(identifier);

  if (!state || state.get(identifier) == null) {
    return null;
  }

  if (!isEncrypted) {
    return state.get(identifier);
  }

  let associatedData = state.get(SharedPreferences.ORG_MEMBER_ID);

  if (
    state.get(SharedPreferences.IS_DEVELOPER) === "true" ||
    state.get(SharedPreferences.IS_USER) === "true" ||
    state.get(SharedPreferences.USER_ID)
  ) {
    associatedData = state.get(SharedPreferences.USER_ID);
  }

  //getting storage key from map
  let storageKey = await getStorageKey(sessionId);

  let decryptedData;

  try {
    let encryptedData = state.get(identifier);

    // review - throw to be removed later
    if (!encryptedData) throw BayunError.ERROR_ENCRYPTED_DATA_NOT_FOUND;

    decryptedData = await aeadDecryptWithStorageKey(
      encryptedData,
      associatedData,
      storageKey,
    );
  } catch (error) {
    if (error === BayunError.ERROR_DECRYPTION_FAILED) {
      console.error("ERROR_DECRYPTION_FAILED");
      clearStorage(sessionId);
    }
    // Rethrow rather than collapsing every failure into ERROR_DECRYPTION_FAILED below. A missing
    // entry (ERROR_ENCRYPTED_DATA_NOT_FOUND, thrown just above) is not a decryption failure, and
    // reporting it as one hid the real cause.
    throw error;
  }

  if (!decryptedData) {
    throw BayunError.ERROR_DECRYPTION_FAILED;
  }
  return decryptedData;
};

/**
 * Invalidates the server-side auth session and clears auth cookies via LMS.
 *
 * @param {userSessionId} sessionId
 */
export const logoutViaServer = async (sessionId) => {
  try {
    const restClient = await getAuthRestclient(sessionId);
    await restClient.post("/logout");
  } catch (error) {
    console.error("Error while logging out on server", error);
  }
};

function getUserIdFromStoredState(sessionId) {
  const stateString = getStoredState(sessionId);
  if (!stateString) {
    return null;
  }

  try {
    const entries = JSON.parse(stateString);
    const userIdEntry = entries.find(
      ([key]) => key === SharedPreferences.USER_ID,
    );
    return userIdEntry ? userIdEntry[1] : null;
  } catch (error) {
    return null;
  }
}

/**
 * Erases the origin-wide session pointer cookies.
 *
 * These name one session for the whole origin, so this cannot be called while logging out a single
 * account among several. It stays only until host apps stop reading `bayunSessionId` and the
 * per-tab active-session pointer replaces it.
 */
export const clearAuthCookies = () => {
  eraseCookie("bayunSessionId");
  eraseCookie(SharedPreferences.LOCAL_STORAGE_ENCRYPTION_MODE);
};

/**
 * Removes everything belonging to one session: its stored data, its registry record, and its
 * in-memory key material. Other sessions on the origin are untouched.
 *
 * The stored data is removed by prefix scan rather than by deleting known keys. Host apps write
 * session-scoped keys the SDK has never heard of, and deleting only the SDK's own keys is how
 * logout came to leave an account's data behind.
 *
 * @param {String} sessionId id of the session to clear.
 */
export const clearStorage = (sessionId) => {
  if (!sessionId) {
    return;
  }

  // Bridge for the pre-namespace linked-members list, which host apps still read and write under a
  // concatenated `sessionId + userId` key. Delete this block once those call sites move onto the
  // storage facade; until then the prefix scan below cannot see the key.
  try {
    const userId = getUserIdFromStoredState(sessionId);
    if (userId) {
      const linkedUsersKey = sessionId + userId;
      window.localStorage.removeItem(linkedUsersKey);
      window.sessionStorage.removeItem(linkedUsersKey);
    }
  } catch (error) {
    // Session state may already be cleared.
  }

  try {
    clearSessionScope(sessionId);
  } catch (error) {
    console.error("Could not clear stored data for session", error);
  }

  // Legacy blobs, for a session that is cleared before it is ever read and therefore never
  // migrated. Remove with migrateLegacyState.
  try {
    window.localStorage.removeItem(sessionId);
    window.sessionStorage.removeItem(sessionId);
  } catch (error) {
    // Nothing to remove.
  }

  // Asked before the record is removed. readActiveSessionId falls back to the registry when the
  // pinned session has no record, and rewrites the pointer to whatever it adopts — so afterwards
  // this can never match, and the tab would be left silently acting as another account.
  const wasActiveHere = readActiveSessionId() === sessionId;

  removeSessionRecord(sessionId);
  clearContext(sessionId);

  // Only this tab's pointer, and only if it was pointing here. Clearing it unconditionally would
  // sign the tab out of an account it had switched to.
  if (wasActiveHere) {
    clearActiveSessionId();
  }

  // The origin-wide cookies name one session, so they can only be erased when no session is left —
  // and they are shared with any other Bayun app on this origin, so the check spans every app's
  // sessions, not just this one's. Erasing them while another account is signed in is what made
  // logging out of one account log every account out.
  if (listAllSessions().length === 0) {
    clearAuthCookies();
  }

  publishSessionEvent(SESSION_EVENTS.SESSION_REMOVED, { sessionId });
};

/**
 * Ends every session in this browser's bundle on the server. One request: any account in the bundle
 * can name it.
 *
 * Does not swallow its error, unlike logoutViaServer. One session left to expire is a small
 * failure; every account in the browser left signed in, while the user is shown a signed-out
 * screen, is not.
 */
export const logoutAllViaServer = async (sessionId) => {
  const restClient = await getAuthRestclient(sessionId);
  await restClient.post("/logoutAll");
};

/**
 * Removes every session on this origin.
 *
 * Separate from clearStorage, which removes exactly one. "Log out" and "log out everywhere" are
 * different actions and must not be the same code path.
 */
export const clearAllStorage = () => {
  // Only this app's sessions. Two Bayun apps can share an origin, and logging out of one is not
  // logging out of the other — clearStorage below removes each session's registry record, so the
  // other app's records survive.
  listSessions().forEach(({ sessionId }) => {
    try {
      clearStorage(sessionId);
    } catch (error) {
      console.error("Could not clear session during logout-all", error);
    }
  });
  clearActiveSessionId();
  publishSessionEvent(SESSION_EVENTS.LOGOUT_ALL, {});
};

/**
 * Resolves the SDK session id: an explicit argument, else this tab's active session.
 *
 * Not the origin-wide sessionId / bayunSessionId cookies. They name whichever account last wrote
 * them, so in a tab that has not chosen an account they would quietly act as some other tab's.
 */
export const resolveSessionId = (sessionId) => sessionId || readActiveSessionId() || "";

async function readCurveFromSessionStorage(sessionId, identifier) {
  if (!sessionId) {
    return null;
  }
  try {
    return await getFromStorage(sessionId, identifier);
  } catch {
    return null;
  }
}

/**
 * Returns the encryption ECC curve stored for the session.
 * Throws rather than returning null, so a missing curve cannot silently fall back to P-384.
 */
export const getEncryptionCurveFromStorage = async (sessionId) => {
  const resolvedSessionId = resolveSessionId(sessionId);
  const curve = await readCurveFromSessionStorage(
    resolvedSessionId,
    SharedPreferences.ECC_CURVE,
  );
  if (!curve) {
    throw new Error("Encryption curve is required.");
  }
  return curve;
};

/**
 * Returns the signing ECC curve stored for the session.
 */
export const getSigningCurveFromStorage = async (sessionId) => {
  const resolvedSessionId = resolveSessionId(sessionId);
  const curve = await readCurveFromSessionStorage(
    resolvedSessionId,
    SharedPreferences.ECC_SIGNING_CURVE,
  );
  if (!curve) {
    throw new Error("Signing curve is required.");
  }
  return curve;
};

/**
 * Persists encryption and signing curves from an LMS auth/registration response.
 */
export const saveEccCurvesFromResponse = async (sessionId, response) => {
  if (response?.eccCurve) {
    await saveInStorage(sessionId, SharedPreferences.ECC_CURVE, response.eccCurve);
  }
  if (response?.eccSigningCurve) {
    await saveInStorage(
      sessionId,
      SharedPreferences.ECC_SIGNING_CURVE,
      response.eccSigningCurve,
    );
  }
};

/**
 * Saves the data in local storage as a state saved against the sessionId.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} identifier the id of the value to be saved in local storage.
 * @param {String} data the data to be saved in local storage.
 */
export const saveInStorage = async (sessionId, identifier, data) => {
  //let state = window.localStorage.getItem(sessionId);
  let state = getStoredState(sessionId);

  if (!state) {
    state = new Map();
  } else {
    state = new Map(JSON.parse(state));
  }

  const isToBeEncrypted = itemsToBeEncrypted.includes(identifier);

  if (isToBeEncrypted && identifier && data) {
    let associatedData = state.get(SharedPreferences.ORG_MEMBER_ID);

    if (
      state.get(SharedPreferences.IS_DEVELOPER) === "true" ||
      state.get(SharedPreferences.IS_USER) === "true" ||
      state.get(SharedPreferences.USER_ID)
    ) {
      associatedData = state.get(SharedPreferences.USER_ID);
    }

    //getting storage key from map
    let storageKey = await getStorageKey(sessionId);

    data = await aeadEncryptWithStorageKey(data, associatedData, storageKey);

    if (!data) {
      throw BayunError.ERROR_ENCRYPTION_FAILED;
    }
  }

  state.set(identifier, data);

  saveState(sessionId, state);
  recordSessionIdentity(sessionId, identifier, data);
};

/**
 * Identity fields the session registry mirrors, so an account switcher can label a session without
 * decrypting its state — or holding its storage key, which a switcher has no business doing.
 *
 * Mirrored on write rather than gathered at login, so a session that acquires an org later is
 * labelled correctly without another call site remembering to update it. None of these are secrets;
 * every one is already shown in the UI.
 */
const REGISTRY_IDENTITY_FIELDS = {
  [SharedPreferences.EMAIL_ADDRESS]: "label",
  [SharedPreferences.ORG_NAME]: "orgName",
  [SharedPreferences.ORG_MEMBER_ID]: "orgMemberId",
  [SharedPreferences.USER_ID]: "userId",
};

function recordSessionIdentity(sessionId, identifier, value) {
  const field = REGISTRY_IDENTITY_FIELDS[identifier];
  if (!field || !value || typeof value !== "string") {
    return;
  }
  try {
    if (getSessionRecord(sessionId)) {
      upsertSessionRecord(sessionId, { [field]: value });
    }
  } catch (error) {
    // A label is a convenience; failing to record one must not fail the write that carried it.
  }
}

/**
 * Keeps only the listed identifiers in a session's state and drops everything else.
 *
 * Host apps used to do this by reading the state blob out of storage, filtering it, and writing it
 * back — which meant duplicating the SDK's storage layout, its choice of storage area, and its key
 * format in application code. Exposing the operation keeps that layout private to the SDK.
 *
 * Retained values are moved across untouched, still in whatever encrypted form they were stored in,
 * so no storage key is needed and nothing is re-encrypted.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String[]} identifiersToKeep identifiers to retain; everything else is dropped.
 */
export const retainInStorage = async (sessionId, identifiersToKeep) => {
  const stateString = getStoredState(sessionId);
  if (!stateString) {
    return;
  }

  const keep = new Set(identifiersToKeep || []);
  const retained = new Map();
  for (const [identifier, value] of new Map(JSON.parse(stateString))) {
    if (keep.has(identifier)) {
      retained.set(identifier, value);
    }
  }

  saveState(sessionId, retained);
};

/**
 * Whether a session has usable stored state.
 *
 * For host apps deciding whether to show a signed-in view on load. Answering this by reading the
 * blob directly is what tied application code to the storage layout.
 *
 * @param {String} sessionId id of the session in question.
 * @returns {Boolean} true when state exists for the session.
 */
export const hasStoredSession = (sessionId) => {
  if (!sessionId) {
    return false;
  }
  try {
    return getStoredState(sessionId) != null;
  } catch (error) {
    return false;
  }
};

/**
 * Returns the equivalent enum object for encryption policy.
 *
 * @param {String} policyAsString the encryption policy as returned from LMS.
 * @returns EncryptionPolicy as the enum object.
 */
export const getEncryptionPolicyFromString = (policyAsString) => {
  switch (policyAsString) {
    case "None":
      return EncryptionPolicy.NONE;

    case "Org":
      return EncryptionPolicy.ORG;

    case "Member":
      return EncryptionPolicy.MEMBER;

    case "Group":
      return EncryptionPolicy.GROUP;
  }
};

/**
 * Returns the equivalent enum object for encryption mode.
 *
 * @param {String} modeAsString the encryption mode as returned from LMS.
 * @returns EncryptionPolicy as the enum object.
 */
export const getEncryptionModeFromString = (modeAsString) => {
  switch (modeAsString) {
    case "CBC":
      return EncryptionMode.CBC;

    case "ECB":
      return EncryptionMode.ECB;

    case "AES-256-GCM":
      return EncryptionMode.AES_GCM;
  }
};

/**
 * Returns the equivalent enum object for key generation policy.
 *
 * @param {String} kgpAsString the KGP as returned from LMS.
 * @returns KeyGenerationPolicy as the enum object.
 */
export const getKeyGenerationPolicyFromString = (kgpAsString) => {
  switch (kgpAsString) {
    case "Static":
      return KeyGenerationPolicy.STATIC;

    case "Envelope":
      return KeyGenerationPolicy.ENVELOPE;

    case "Chain":
      return KeyGenerationPolicy.CHAIN;
  }
};

/**
 *
 * @param {*} sessionId
 * @param {*} signatureVerificationKeyTag
 * @param {*} groupId
 */
export const getSignatureVerificationPublicKey = async (
  sessionId,
  signatureVerificationKeyTag,
  groupId,
) => {
  // Signatures are always produced with signing keys, so every identity tag resolves to a
  // signing public key. Encryption public keys are never used to verify a signature.
  if (
    signatureVerificationKeyTag == SigningPublicKeyTags.MEMBER_PUBLIC_KEY_TAG ||
    signatureVerificationKeyTag ==
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_SIGNING_PUBLIC_KEY,
    );
  } else if (
    signatureVerificationKeyTag == SigningPublicKeyTags.ADMIN_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(
      sessionId,
      SharedPreferences.ADMIN_SIGNING_PUBLIC_KEY,
    );
  } else if (
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.BAYUN_SERVER_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(
      sessionId,
      SharedPreferences.BAYUN_SERVER_PUBLIC_KEY,
    );
  } else if (
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.MEMBER_APP_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_SIGNING_PUBLIC_KEY,
    );
  } else if (
    signatureVerificationKeyTag == SigningPublicKeyTags.ORG_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(
      sessionId,
      SharedPreferences.ORG_SIGNING_PUBLIC_KEY,
    );
  } else if (
    signatureVerificationKeyTag == SigningPublicKeyTags.GROUP_PUBLIC_KEY_TAG
  ) {
    return ""; //MemberClientDetails::getGroupPublicKey(sessionId, groupId);
  } else if (
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.MEMBER_CREATION_APP_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_CREATION_APP_SIGNING_PUBLIC_KEY,
    );
  } else if (
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.AUTHORIZATION_APP_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(
      sessionId,
      SharedPreferences.AUTHORISATION_APP_SIGNING_PUBLIC_KEY,
    );
  } else if (
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.GHOST_MEMBER_PUBLIC_KEY_TAG ||
    signatureVerificationKeyTag ==
      SigningPublicKeyTags.GHOST_MEMBER_ENCRYPTION_PUBLIC_KEY_TAG
  ) {
    // Both ghost tags resolve here. The tag distinguishes which half of the pair a layer carries,
    // but either layer was signed with the ghost signing private key, so the signing public key is
    // what checks both. Without this branch the encryption tag fell through to "" and its chain
    // was unverifiable - the layer existed but nothing could ever confirm it.
    return await getFromStorage(
      sessionId,
      SharedPreferences.GHOST_MEMBER_SIGNING_PUBLIC_KEY,
    );
  } else if (
    signatureVerificationKeyTag == SigningPublicKeyTags.USER_PUBLIC_KEY_TAG ||
    signatureVerificationKeyTag ==
      SigningPublicKeyTags.USER_SIGNING_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(sessionId, SharedPreferences.USER_SIGNING_PUBLIC_KEY);
  } else if (
    signatureVerificationKeyTag == SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(sessionId, SharedPreferences.ADMIN_SIGNING_PUBLIC_KEY);
  } else if (
    signatureVerificationKeyTag == SigningPublicKeyTags.BACKDOOR_SIGNING_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(sessionId, SharedPreferences.BACKDOOR_SIGNING_PUBLIC_KEY);
  } else if (
    signatureVerificationKeyTag == SigningPublicKeyTags.MEMBER_APP_SIGNING_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(sessionId, SharedPreferences.MEMBER_APP_SIGNING_PUBLIC_KEY);
  } else if (
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.MEMBER_CREATION_APP_SIGNING_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_CREATION_APP_SIGNING_PUBLIC_KEY,
    );
  } else if (
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.AUTHORIZATION_APP_SIGNING_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(
      sessionId,
      SharedPreferences.AUTHORISATION_APP_SIGNING_PUBLIC_KEY,
    );
  } else if (
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.ACCESS_APP_SIGNING_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(sessionId, SharedPreferences.ACCESS_APP_SIGNING_PUBLIC_KEY);
  } else if (
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.FIRST_MEMBER_PUBLIC_KEY_TAG ||
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.APPROVING_MEMBER_PUBLIC_KEY_TAG ||
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.GROUP_CREATOR_PUBLIC_KEY_TAG ||
    signatureVerificationKeyTag ==
    SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG
  ) {
    // Group creator / adding-member signatures are produced with the member signing key.
    // Callers normally pass the peer's signing key explicitly; this is the local fallback.
    return await getFromStorage(sessionId, SharedPreferences.MEMBER_SIGNING_PUBLIC_KEY);
  } else if (
    signatureVerificationKeyTag == SigningPublicKeyTags.BACKDOOR_PUBLIC_KEY_TAG
  ) {
    return await getFromStorage(sessionId, SharedPreferences.BACKDOOR_SIGNING_PUBLIC_KEY);
  }
  return "";
};

/**
 * This method returns the corresponding SDK error message for the given LMS error message.
 *
 * @param {String} errorMessageFromLms The error data returned from LMS on API calls.
 * @returns {String} SDK error message.
 */
/**
 * A session the server will not accept again: tell whoever is listening, then drop its stored data.
 *
 * Order matters. clearStorage removes the session's registry record, and that record is how
 * reportReauthenticationNeeded tells a finished sign-in from a login attempt that never got there —
 * so clearing first would silence the report entirely.
 */
const endSession = (sessionId) => {
  reportReauthenticationNeeded(sessionId);
  clearStorage(sessionId);
};

export const getErrorMessage = (sessionId, errorMessageFromLms) => {
  // If the value has already been mapped to a BayunError, return it as-is.
  // Re-mapping an already-converted BayunError would fail to match any LMS
  // substring and incorrectly degrade it to ERROR_SOMETHING_WENT_WRONG.
  if (
    typeof errorMessageFromLms === 'string' &&
    Object.values(BayunError).includes(errorMessageFromLms)
  ) {
    return errorMessageFromLms;
  }
  // Plain Error objects stringify to "{}" (message is non-enumerable).
  // Prefer .message so rethrown LMS text still maps correctly.
  if (errorMessageFromLms instanceof Error) {
    errorMessageFromLms = errorMessageFromLms.message;
  }
  if (typeof errorMessageFromLms !== 'string') {
    errorMessageFromLms = JSON.stringify(errorMessageFromLms);
  }
  const LMS_TO_BAYUN_ERROR_MAP = [
    {
      substr: LmsErrorConstants.INVALID_CREDENTIALS,
      code: BayunError.ERROR_INVALID_CREDENTIALS,
    },
    {
      substr: LmsErrorConstants.INCORRECT_PASSCODE,
      code: BayunError.ERROR_INVALID_PASSCODE,
    },
    {
      substr: LmsErrorConstants.INCORRECT_PASSPHRASE,
      code: BayunError.ERROR_INVALID_PASSPHRASE,
    },
    {
      substr: LmsErrorConstants.MEMBER_DEACTIVATED,
      code: BayunError.ERROR_USER_INACTIVE,
    },
    {
      substr: LmsErrorConstants.APP_NOT_EXISTS_FOR_ID,
      code: BayunError.ERROR_INVALID_APP_ID,
    },
    {
      substr: LmsErrorConstants.INVALID_APP_ID,
      code: BayunError.ERROR_INVALID_APP_ID,
    },
    {
      substr: LmsErrorConstants.ORG_DOES_NOT_EXIST_FOR_NAME,
      code: BayunError.ERROR_INVALID_ORG_NAME,
    },
    {
      substr: LmsErrorConstants.MINIMUM_THREE_ANSWERS_NEEDED,
      code: BayunError.ERROR_AT_LEAST_THREE_ANSWERS_REQUIRED,
    },
    {
      substr: LmsErrorConstants.WRONG_ANSWERS_ENTERED,
      code: BayunError.ERROR_INCORRECT_ANSWERS,
    },
    {
      substr: LmsErrorConstants.GROUP_NOT_EXISTS_FOR_ID,
      code: BayunError.ERROR_INVALID_GROUP_ID,
    },
    {
      substr: LmsErrorConstants.MEMBER_DOES_NOT_EXIST_FOR_ID,
      code: BayunError.ERROR_MEMBER_DOESNT_EXIST,
    },
    {
      substr: LmsErrorConstants.MEMBER_NOT_EXISTS_FOR_ID_AND_ORG_NAME,
      code: BayunError.ERROR_MEMBER_DOESNT_EXIST,
    },
    {
      substr: LmsErrorConstants.MEMBERS_NOT_EXISTS_FOR_ID_AND_ORG_NAME,
      code: BayunError.ERROR_MEMBERS_DOESNT_EXIST,
    },
    {
      substr: LmsErrorConstants.MEMBER_NOT_EXISTS_IN_GROUP,
      code: BayunError.ERROR_MEMBER_DOESNT_BELONG_TO_GROUP,
    },
    {
      substr: LmsErrorConstants.MEMBER_NOT_LINKED_WITH_GROUP_ID,
      code: BayunError.ERROR_MEMBER_DOESNT_BELONG_TO_GROUP,
    },
    {
      substr: LmsErrorConstants.MEMBER_NOT_LINKED_WITH_GROUP,
      code: BayunError.ERROR_MEMBER_DOESNT_BELONG_TO_GROUP,
    },
    {
      substr: LmsErrorConstants.NOT_PARTICIPANT_OF_GROUP,
      code: BayunError.ERROR_MEMBER_DOESNT_BELONG_TO_GROUP,
    },
    {
      substr: LmsErrorConstants.MEMBER_EXISTS_IN_GROUP,
      code: BayunError.ERROR_PARTICIPANT_EXISTS_IN_GROUP,
    },
    {
      substr: LmsErrorConstants.MEMBER_TO_REMOVE_NOT_EXISTS,
      code: BayunError.ERROR_MEMBER_DOESNT_EXIST,
    },
    {
      substr: LmsErrorConstants.CANNOT_JOIN_GROUP_NOT_PUBLIC,
      code: BayunError.ERROR_CANNOT_JOIN_PRIVATE_GROUP,
    },
    {
      substr: LmsErrorConstants.GROUP_ID_NOT_BELONGS_TO_PUBLIC_GROUP,
      code: BayunError.ERROR_CANNOT_JOIN_PRIVATE_GROUP,
    },
    {
      substr: LmsErrorConstants.INVALID_SECRET,
      code: BayunError.ERROR_INVALID_APP_SECRET,
    },
    // The credential itself is unusable, so the session is over: tell the host, then drop what it
    // stored. Reporting has to come first, because clearing removes the registry record that
    // reportReauthenticationNeeded needs to know this was a real signed-in account.
    {
      substr: LmsErrorConstants.INVALID_TOKEN,
      code: BayunError.ERROR_REAUTHENTICATION_NEEDED,
      onMatch: (sessionIdParam) => endSession(sessionIdParam),
    },
    {
      substr: LmsErrorConstants.EXPIRED_TOKEN,
      code: BayunError.ERROR_REAUTHENTICATION_NEEDED,
      onMatch: (sessionIdParam) => endSession(sessionIdParam),
    },
    // Not the same thing, and it used to be treated as though it were.
    //
    // Every server site that says this has already resolved the session and found the credential
    // good; what it is refusing is the session's *status* — part-way through registering, or through
    // recovery. A missing or expired credential produces the two messages above instead.
    //
    // Mapping it to "sign in again" mislabelled a flow that was working, and the storage wipe that
    // came with it destroyed the half-finished sign-in it was complaining about. Registering a new
    // member failed exactly this way: the passphrase was accepted, the very next call was refused
    // for status, and the client threw away the keys it had just been given.
    {
      substr: LmsErrorConstants.RE_AUTHENTICATE,
      code: BayunError.ERROR_SESSION_NOT_READY,
    },
    {
      substr: LmsErrorConstants.INVALID_KEY_PAIR,
      code: BayunError.ERROR_INVALID_KEY_PAIR,
    },
    {
      substr: LmsErrorConstants.MEMBER_APP_NOT_EXIST_FOR_ID,
      code: BayunError.ERROR_MEMBER_APP_NOT_EXIST_FOR_ID,
    },
    {
      substr: LmsErrorConstants.MEMBER_PUBLIC_KEY_NOT_EXISTS,
      code: BayunError.ERROR_MEMBER_PUBLIC_KEY_NOT_EXISTS,
    },
    {
      substr: LmsErrorConstants.USER_NOT_ASSOCIATED_WITH_MEMBER,
      code: BayunError.ERROR_USER_NOT_ASSOCIATED_WITH_MEMBER,
    },
    {
      substr: LmsErrorConstants.MEMBER_LOCK_BOX_NOT_FOUND_FOR_MEMBER,
      code: BayunError.ERROR_MEMBER_LOCK_BOX_NOT_FOUND_FOR_MEMBER,
    },
    {
      substr: LmsErrorConstants.MEMBER_CREATION_APP_KEY_PAIR_ID_REQUIRED,
      code: BayunError.ERROR_MEMBER_CREATION_APP_KEY_PAIR_ID_REQUIRED,
    },
    {
      substr: LmsErrorConstants.ACCESS_APP_KEY_PAIR_ID_REQUIRED,
      code: BayunError.ERROR_ACCESS_APP_KEY_PAIR_ID_REQUIRED,
    },
    {
      substr: LmsErrorConstants.APP_NOT_LINKED,
      code: BayunError.ERROR_APP_NOT_LINKED,
    },
    {
      substr: LmsErrorConstants.MEMBER_APP_KEY_PAIR_ALREADY_EXISTS,
      code: BayunError.ERROR_MEMBER_APP_KEY_PAIR_ALREADY_EXISTS,
    },
    {
      substr: LmsErrorConstants.MEMBER_APP_NOT_EXISTS_FOR_AUTH_TOKEN,
      code: BayunError.ERROR_ACCESS_DENIED,
    },
    {
      substr: LmsErrorConstants.MEMBER_NOT_EXISTS_FOR_MEMBER_APP,
      code: BayunError.ERROR_ACCESS_DENIED,
    },
    {
      substr: LmsErrorConstants.MEMBER_APPROVAL_IS_PENDING,
      code: BayunError.ERROR_MEMBER_NOT_APPROVED_YET,
    },
    {
      substr: LmsErrorConstants.MEMBER_APP_APPROVAL_IS_PENDING,
      code: BayunError.ERROR_MEMBER_APP_NOT_APPROVED,
    },
    {
      substr: LmsErrorConstants.INCORRECT_SECRET,
      code: BayunError.ERROR_INCORRECT_SECRET,
    },
    {
      substr: LmsErrorConstants.ACCOUNT_PENDING_AUTHORIZATION,
      code: BayunError.ERROR_MEMBER_AUTHORIZATION_IS_PENDING,
    },
    {
      substr: LmsErrorConstants.MEMBER_AUTHORIZATION_IS_PENDING,
      code: BayunError.ERROR_MEMBER_AUTHORIZATION_IS_PENDING,
    },
    {
      substr: LmsErrorConstants.EMPLOYEE_AUTHORIZATION_IS_PENDING,
      code: BayunError.ERROR_MEMBER_AUTHORIZATION_IS_PENDING,
    },
    {
      substr: LmsErrorConstants.ORG_APP_NOT_APPROVED,
      code: BayunError.ERROR_MEMBER_APP_NOT_APPROVED,
    },
    {
      substr: LmsErrorConstants.INVALID_SERVER_PUBLIC_KEY,
      code: BayunError.ERROR_INVALID_SERVER_PUBLIC_KEY,
    },
    {
      substr: LmsErrorConstants.ERROR_ORG_OR_MEMBER_OR_MEMBERAPP_ALREADY_EXIST,
      code: BayunError.ERROR_ORG_OR_MEMBER_OR_MEMBERAPP_ALREADY_EXIST,
    },
    {
      substr: LmsErrorConstants.USER_DOSE_NOT_EXIST,
      code: BayunError.ERROR_USER_DOSE_NOT_EXISTS,
    },
    {
      substr: LmsErrorConstants.ERROR_TOKEN_NOT_FOUND,
      code: BayunError.ERROR_TOKEN_NOT_FOUND,
    },
    {
      substr: LmsErrorConstants.INVALID_ORG_OR_MEMBER_NAME,
      code: BayunError.INVALID_ORG_OR_MEMBER_NAME,
    },
    {
      substr: LmsErrorConstants.APP_NOT_EXIST_FOR_ORG,
      code: BayunError.APP_NOT_EXIST_FOR_ORG,
    },
    {
      substr: LmsErrorConstants.RE_REGISTER,
      code: BayunError.RE_REGISTER,
    },
    {
      substr: LmsErrorConstants.USER_REGISTRATION_PENDING,
      code: BayunError.USER_REGISTRATION_PENDING,
    },
    {
      substr: LmsErrorConstants.APP_DOES_NOT_HAVE_THE_CORRECT_ROLE,
      code: BayunError.APP_DOES_NOT_HAVE_THE_CORRECT_ROLE,
    },
    {
      substr: LmsErrorConstants.ORG_CREATION_APP_KEY_PAIR_ID_REQUIRED,
      code: BayunError.APP_DOES_NOT_HAVE_THE_CORRECT_ROLE,
    },
    {
      substr: LmsErrorConstants.INVALID_KEY_PAIR_ID,
      code: BayunError.INVALID_KEY_PAIR_ID,
    },
    {
      substr: LmsErrorConstants.AUTO_APPROVED_MEMBER_ACCESS_DENIED,
      code: BayunError.AUTO_APPROVED_MEMBER_ACCESS_DENIED,
    },
    {
      substr: LmsErrorConstants.MEMBER_NOT_APPROVED_YET,
      code: BayunError.ERROR_MEMBER_NOT_APPROVED_YET,
    },
    {
      substr: LmsErrorConstants.MEMBER_ALREADY_EXISTS_FOR_A_DIFFERENT_EMAIL_ADDRESS,
      code: BayunError.ERROR_MEMBER_ALREADY_EXISTS_FOR_A_DIFFERENT_EMAIL_ADDRESS,
    },
    {
      substr: LmsErrorConstants.MEMBER_ALREADY_EXISTS,
      code: BayunError.ERROR_MEMBER_ALREADY_EXISTS,
    },
    {
      substr: LmsErrorConstants.ORGANIZATION_ALREADY_EXISTS,
      code: BayunError.ERROR_ORGANIZATION_ALREADY_EXISTS,
    },
    {
      substr: LmsErrorConstants.REGISTRATION_INCOMPLETE,
      code: BayunError.ERROR_REGISTRATION_INCOMPLETE,
    },
    {
      substr: LmsErrorConstants.ERROR_USER_HAVE_NO_ACTIVE_MEMBERS,
      code: BayunError.ERROR_USER_HAVE_NO_ACTIVE_MEMBERS,
    },
    {
      substr: LmsErrorConstants.USER_RECOVERY_ALREADY_IN_PROGRESS,
      code: BayunError.ERROR_USER_RECOVERY_ALREADY_IN_PROGRESS,
    },
    {
      substr: LmsErrorConstants.USER_HAS_DEVELOPER_ACCOUNT_DURING_RECOVERY,
      code: BayunError.ERROR_USER_HAS_DEVELOPER_ACCOUNT_DURING_RECOVERY,
    },
    {
      substr: LmsErrorConstants.CANNOT_LINK_DEVELOPER_ACCOUNT_DURING_RECOVERY,
      code: BayunError.ERROR_CANNOT_LINK_DEVELOPER_ACCOUNT_DURING_RECOVERY,
    },
  ];

  const match = LMS_TO_BAYUN_ERROR_MAP.find((entry) =>
    errorMessageFromLms.includes(entry.substr),
  );

  if (match) {
    if (typeof match.onMatch === 'function') {
      match.onMatch(sessionId);
    }
    return match.code;
  }

  return BayunError.ERROR_SOMETHING_WENT_WRONG;
};

/**
 *
 * @param {*} orgName
 * @param {*} orgKey
 */
export const createLMSOrgKey = async (orgName, orgKey) => {
  if (orgName && orgKey) {
    const baseId = "lms" + orgName;
    return await hmacSha256Raw(orgKey, baseId);
  }
  return null;
};

export const createLMSGroupKey = async (groupKey, orgName, orgMemberId) => {
  if (groupKey && orgName && orgMemberId) {
    const baseId = "lms" + orgName + orgMemberId;
    return await hmacSha256Raw(groupKey, baseId);
  }
  return null;
};

/**
 * This method splits a string into 5 parts using SSS (Shamir Secret Sharing), used for user private key
 *
 * @param {String} stringToBeSplit The string to be split into parts.
 * @returns {Map} A map containing the split parts with keys "1", "2", "3", "4", "5".
 */
export const splitUsingSSS = (stringToBeSplit) => {
  const utf8Encoder = new TextEncoder();
  const splitParts = split(
    crypto.randomBytes,
    5,
    3,
    utf8Encoder.encode(stringToBeSplit),
  );

  const splitPartsMap = new Map(Object.entries(splitParts));

  for (let i = 0; i < 5; i++) {
    splitPartsMap.set(
      `${i + 1}`,
      concatTypedArrays(
        convertIntToBytes(i + 1),
        splitPartsMap.get(`${i + 1}`),
      ),
    );
  }

  return splitPartsMap;
};

/**
 * Splits a string into n parts using Shamir Secret Sharing algorithm.
 * Requires k parts to reconstruct the original secret.
 *
 * @param {String} stringToBeSplit The string to be split into parts.
 * @param {Number} n Total number of parts to create (must be >= 2).
 * @param {Number} k Minimum number of parts required to reconstruct (must be >= 2).
 * @returns {Map} A map containing the split parts with keys "1", "2", ..., "n".
 */
export const splitUsingSSSDynamic = (stringToBeSplit, n, k) => {
  if (n < 2) {
    throw new Error("N must be >= 2");
  }
  if (k < 2) {
    throw new Error("K must be >= 2");
  }
  if (k > n) {
    throw new Error("K must be <= N");
  }

  const utf8Encoder = new TextEncoder();
  const splitParts = split(
    crypto.randomBytes,
    n,
    k,
    utf8Encoder.encode(stringToBeSplit),
  );

  const splitPartsMap = new Map(Object.entries(splitParts));

  for (let i = 0; i < n; i++) {
    splitPartsMap.set(
      `${i + 1}`,
      concatTypedArrays(
        convertIntToBytes(i + 1),
        splitPartsMap.get(`${i + 1}`),
      ),
    );
  }

  return splitPartsMap;
};

const SHARE_INDEX_PREFIX_LENGTH = 4;

function stripShareIndexPrefix(shareBytes) {
  if (shareBytes.length <= SHARE_INDEX_PREFIX_LENGTH) {
    return shareBytes;
  }
  return shareBytes.subarray(SHARE_INDEX_PREFIX_LENGTH);
}

/**
 * Strips the 4-byte share index prefix added by splitUsingSSS before shamir join.
 *
 * @param {Map<string, Uint8Array>|Object.<string, Uint8Array>} parts
 * @returns {Object.<string, Uint8Array>}
 */
export const prepareSharesForJoin = (parts) => {
  const entries =
    parts instanceof Map ? [...parts.entries()] : Object.entries(parts);
  return Object.fromEntries(
    entries.map(([key, value]) => [key, stripShareIndexPrefix(value)]),
  );
};

/** Joins shares produced by splitUsingSSS / splitUsingSSSDynamic (strips index prefix first). */
export const joinPrefixedShares = (parts) => join(prepareSharesForJoin(parts));

/**
 * Formats a recovered secret as a PEM private key (discards the first line, rebuilds header).
 *
 * @param {String} recoveredString UTF-8 string from joinPrefixedShares
 * @returns {String}
 */
export const formatRecoveredAsPemPrivateKey = (recoveredString) => {
  const lines = recoveredString.split("\n");
  let privateKey = "-----BEGIN PRIVATE KEY-----";
  for (let i = 1; i < lines.length; i++) {
    privateKey = privateKey + "\n" + lines[i];
  }
  return privateKey;
};

export const joinUsingSSS = (splitPartsMap) => {
  const joined = joinPrefixedShares(splitPartsMap);
  return formatRecoveredAsPemPrivateKey(convertArrayBufferToString(joined));
};

function concatTypedArrays(a, b) {
  var c = new a.constructor(a.length + b.length);
  c.set(a, 0);
  c.set(b, a.length);
  return c;
}

function convertIntToBytes(num) {
  let arr = new ArrayBuffer(4); // an Int32 takes 4 bytes
  let view = new DataView(arr);
  view.setUint32(0, num, false); // byteOffset = 0; litteEndian = false
  return new Uint8Array(arr);
}

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Converts the given ArrayBuffer into Uint8Buffer.
 *
 * @param {ArrayBuffer} ab the arraybuffer to be convered.
 * @returns uint8 buffer.
 */
export const toBuffer = (ab) => {
  var buf = Buffer.alloc(ab.byteLength);
  var view = new Uint8Array(ab);
  for (var i = 0; i < buf.length; ++i) {
    buf[i] = view[i];
  }
  return buf;
};

export const toArrayBuffer = (buf) => {
  var ab = new ArrayBuffer(buf.length);
  var view = new Uint8Array(ab);
  for (var i = 0; i < buf.length; ++i) {
    view[i] = buf[i];
  }
  return ab;
};

/**
 * Checks if the given string is valid UTF-8.
 *
 * @param input input to be checked.
 * @return True/False.
 */
export const isValidUTF8 = (input) => {
  try {
    // decodeURIComponent(escape(...)) throws only for invalid UTF-8.
    decodeURIComponent(escape(input));
    return true;
  } catch (e) {
    return false;
  }
};

async function initializeStorageKey(sessionId) {
  try {
    const uaData = navigator.userAgentData;
    const browserInfo = {
      userAgentData:
        uaData?.brands?.[0]?.brand ?? navigator.userAgent,
    };

    const deviceInfo = {
      devicePixelHeight: window.screen.height,
      devicePixelWidth: window.screen.width,
      hardwareConcurrency: navigator.hardwareConcurrency,
      platform: uaData?.platform ?? navigator.platform,
    };

    const memberAppId = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_ID,
    );

    const userId = await getFromStorage(sessionId, SharedPreferences.USER_ID);

    //todo: need to check what to send in place of memberId if both are present
    const queryObject = {
      ...browserInfo,
      ...deviceInfo,
    };

    if (!memberAppId && !userId) {
      // Fail closed. Returning undefined here made getStorageKey resolve to undefined, and
      // saveInStorage then handed that to aeadEncryptWithStorageKey, which base64-decodes the
      // key — surfacing as an opaque "atob: string not correctly encoded" far from the cause.
      console.error(
        "Cannot fetch sdk storage key: neither memberAppId nor userId is in storage yet.",
      );
      throw BayunError.ERROR_STORAGE_KEY_NOT_FOUND_IN_RESPONSE;
    }

    if (
      process.env.SOURCE === Constants.SOURCE.BAYUN_ADMIN &&
      (await getFromStorage(sessionId, SharedPreferences.IS_PASSCODE_AUTH)) !==
      "true"
    ) {
      // add userId in queryObject
      queryObject.userId = userId;
    } else {
      // add memberAppId in queryObject
      queryObject.memberAppId = memberAppId;
    }

    const restClient = await getAuthRestclient(sessionId);
    const response = await restClient.get("/sdkStorageKey", {
      params: queryObject, // Axios automatically URL-encodes each key-value pair
    });
    if (response.data.storageKey) {
      setStorageKeyFor(sessionId, response.data.storageKey);
    } else {
      throw BayunError.ERROR_STORAGE_KEY_NOT_FOUND_IN_RESPONSE;
    }

    // Deliberately not logged. storageKey is the AEAD key for every entry in itemsToBeEncrypted
    // (member/user/admin/backdoor signing private keys, orgKey, memberKey). In
    // EXPLICIT_LOGOUT_MODE the ciphertext lives in window.localStorage, so logging the key put key
    // and ciphertext in the same place and defeated encryption at rest.

    return response.data.storageKey;
  } catch (error) {
    console.error("Error on fetching sdk storage key", error);
    throw error;
  }
}

/**
 * Completes a successful sign-in: stamps the session as signed in, bounds how many accounts are
 * resident in this page (decision D3), and tells other tabs.
 *
 * Runs on success rather than at the start of login, because only a finished sign-in is an account.
 * The session just signed in is the most recently active, so it is never the one evicted.
 *
 * Evicted accounts are ended on the server too rather than left to expire. That has to come before
 * the local teardown, because the request needs the account key and stored appId eviction removes.
 * logoutViaServer swallows its own failures, so an unreachable server cannot block the eviction
 * that bounds memory.
 *
 * @param {String} sessionId the session that just signed in.
 */
export const finalizeSignedInSession = async (sessionId) => {
  if (!sessionId) {
    return;
  }
  const wasSignedIn = Boolean(getSessionRecord(sessionId)?.signedInAt);
  markSignedIn(sessionId);

  for (const doomedSessionId of sessionsOverCap()) {
    await logoutViaServer(doomedSessionId);
  }
  enforceSessionCap().forEach((evictedSessionId) => {
    publishSessionEvent(SESSION_EVENTS.SESSION_REMOVED, { sessionId: evictedSessionId });
  });

  // Re-authenticating an account that was already signed in adds nothing to the switcher.
  if (!wasSignedIn) {
    publishSessionEvent(SESSION_EVENTS.SESSION_ADDED, { sessionId });
  }
};

/**
 * A LocalDataEncryptionMode as a number, or null when the caller named none.
 *
 * Null is not the default: it means "leave this session's mode alone". Collapsing the two is what
 * made an omitted argument wipe session state.
 */
function toStorageMode(mode) {
  if (mode === null || mode === undefined || mode === "") {
    return null;
  }
  const asNumber = Number(mode);
  return Number.isFinite(asNumber) ? asNumber : null;
}

export const initAuthenticationProcess = async (
  sessionId,
  localDataEncryptionMode,
) => {
  let isNewAuthentication = true;
  let requestedMode = toStorageMode(localDataEncryptionMode);

  if (!sessionId) {
    sessionId = generateRandomUuid();
  } else {
    // A registry record plus a readable state blob means the account never logged out and this is
    // a reauthentication for the local encryption key. This replaces comparing the sessionId
    // against the origin-wide `bayunSessionId` cookie, which could only ever recognise one session.
    const existingRecord = getSessionRecord(sessionId);
    if (existingRecord && getStoredState(sessionId)) {
      isNewAuthentication = false;

      // No mode named means no change. An omitted argument used to become NaN, which never equals
      // the stored mode, so every re-authentication took the wipe branch below.
      if (requestedMode === null) {
        requestedMode = toStorageMode(existingRecord.mode);
      }

      // The mode is per session now. If a session comes back under a different mode its state is in
      // the other storage area, where nothing will find it again, so clear it and start fresh
      // rather than stranding an unreadable blob.
      if (requestedMode !== toStorageMode(existingRecord.mode)) {
        clearSessionScope(sessionId);
        isNewAuthentication = true;
      }
    }
  }

  upsertSessionRecord(sessionId, {
    mode: requestedMode === null ? LocalDataEncryptionMode.SESSION_MODE : requestedMode,
  });

  // This tab now acts as the session it just authenticated. Other tabs keep their own, and keep
  // working: the server addresses each account in the bundle separately, so authenticating here no
  // longer takes anything away from them.
  setActiveSessionId(sessionId);

  // This runs before any credential is checked, so the record written above may belong to an
  // attempt that is about to fail. Clear away the ones that already did.
  reapAbandonedSessions().forEach((reapedSessionId) => {
    publishSessionEvent(SESSION_EVENTS.SESSION_REMOVED, { sessionId: reapedSessionId });
  });

  // Eviction and the session-added event wait for finalizeSignedInSession: nothing here knows yet
  // whether this attempt will succeed.

  // Origin-wide session pointer, kept only until host apps stop reading `bayunSessionId`. The
  // registry and the per-tab pointer above are what the SDK itself uses.
  setCookie("bayunSessionId", sessionId, 1);

  // The MFA-setting refresh check that used to live here was already commented out. Its helper
  // (didMFASettingUpdate) was the only reason util/Utility.js imported from rest/BayunAPIManager,
  // creating a util -> rest -> util cycle, so it has been removed with the dead call site. Restore
  // both together if the check is needed again.

  await saveInStorage(
    sessionId,
    SharedPreferences.IS_NEW_AUTHENTICATION,
    isNewAuthentication,
  );

  return sessionId;
};

export const binaryToString = (array) => {
  return String.fromCharCode.apply(String, array);
};

export const stringToBinary = (str) => {
  var result = [];
  for (var i = 0; i < str.length; i++) {
    result.push(str.charCodeAt(i));
  }
  return result;
};

/**
 * Field names whose values must never reach the console. Matching is case-insensitive and by
 * substring, so `memberSigningPrivateKey`, `orgPrivateKey_kek` and `storageKey` are all covered.
 */
const REDACTED_LOG_FIELD_PATTERNS = [
  "privatekey",
  "storagekey",
  "passcode",
  "passphrase",
  "password",
  "secret",
  "authtoken",
  "_kek",
  "kek",
  "orgkey",
  "memberkey",
  "userkey",
  "answer",
  "salt",
];

const isRedactedLogField = (key) => {
  const normalized = String(key).toLowerCase();
  return REDACTED_LOG_FIELD_PATTERNS.some((pattern) =>
    normalized.includes(pattern),
  );
};

/**
 * Deep-copies a value, replacing anything that looks like key material with a placeholder.
 *
 * API responses carry wrapped private keys and KEKs, and the storage key that unwraps them was
 * previously logged too, so console output alone was enough to recover plaintext keys.
 */
const redactForLogging = (value, depth = 0) => {
  if (depth > 6 || value == null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => redactForLogging(entry, depth + 1));
  }
  const redacted = {};
  for (const [key, entryValue] of Object.entries(value)) {
    redacted[key] = isRedactedLogField(key)
      ? "[redacted]"
      : redactForLogging(entryValue, depth + 1);
  }
  return redacted;
};

/**
 * This function logs the API response.
 *
 * @param {Object} API response
 */
export const logApiResponse = async (responseOf, response) => {
  console.log(responseOf, ": ", redactForLogging(response?.data ?? response));
};

export function setCookie(name, value, days) {
  var expires = "";
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  var cookieValue = value !== undefined && value !== null ? value : "";
  var isSecure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie =
    name +
    "=" +
    cookieValue +
    expires +
    "; path=/; SameSite=Lax" +
    isSecure;
}

export function getCookie(name) {
  var nameEQ = name + "=";
  var ca = document.cookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function eraseCookie(name) {
  document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
}

export function setStorageKey(sessionId, value) {
  setStorageKeyFor(sessionId, value);
}

export async function getStorageKey(sessionId) {
  const cached = getStorageKeyFor(sessionId);
  if (cached) {
    return cached;
  }
  return await initializeStorageKey(sessionId);
}

export const isValidEmail = (email) => {
  // Regular expression to validate email
  const mailformat =
    /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
  return mailformat.test(email);
};

export async function getStorageEncryptionKey(sessionId) {
  if (getStorageKeyFor(sessionId)) {
    return;
  }

  await initializeStorageKey(sessionId);
  const storageKey = getStorageKeyFor(sessionId);
  if (!storageKey) {
    reportReauthenticationNeeded(sessionId);
    throw BayunError.ERROR_REAUTHENTICATION_NEEDED;
  }

  const staged = getInternalStorageMap(sessionId);
  if (!staged) {
    return;
  }

  await encryptWithStorageKey(sessionId, staged, storageKey);
}

async function encryptWithStorageKey(sessionId, internalStorage, storageKeyHolder) {
  let storedState = getStoredState(sessionId);

  storedState = new Map(JSON.parse(storedState));

  const orgMemberId = storedState.get(SharedPreferences.ORG_MEMBER_ID);

  for (const identifier of internalStorage.keys()) {
    const isEncrypted = itemsToBeEncrypted.includes(identifier);

    if (!isEncrypted) {
      storedState.set(identifier, internalStorage.get(identifier));
      continue;
    }

    try {
      // encrypt from permanent storage key
      var encryptedData = await aeadEncryptWithStorageKey(
        internalStorage.get(identifier),
        orgMemberId,
        storageKeyHolder,
      );
      storedState.set(identifier, encryptedData);
    } catch (error) {
      console.error(error);
      break;
    }
  }
  saveState(sessionId, storedState);
}

function saveState(sessionId, state) {
  writeSessionValue(
    sessionId,
    STATE_KEY,
    JSON.stringify(Array.from(state.entries())),
    getSessionMode(sessionId),
  );
}

/**
 * Reads a session's state blob, migrating it out of the pre-namespace layout on first sight.
 *
 * Sessions created before namespacing stored the blob under the bare sessionId, in localStorage or
 * sessionStorage depending on the origin-wide mode cookie. Those sessions are still live — the
 * refresh token lasts seven days — so they are adopted rather than signed out on upgrade.
 *
 * The area the blob is found in is authoritative for the session's mode, because that is what the
 * old code used it for.
 */
function migrateLegacyState(sessionId) {
  if (!sessionId) {
    return null;
  }

  const candidates = [
    [window.localStorage, LocalDataEncryptionMode.EXPLICIT_LOGOUT_MODE],
    [window.sessionStorage, LocalDataEncryptionMode.SESSION_MODE],
  ];

  for (const [area, mode] of candidates) {
    let legacy;
    try {
      legacy = area.getItem(sessionId);
    } catch (error) {
      continue;
    }
    if (legacy == null) {
      continue;
    }

    upsertSessionRecord(sessionId, { mode });
    writeSessionValue(sessionId, STATE_KEY, legacy, mode);
    try {
      area.removeItem(sessionId);
    } catch (error) {
      // Leaving the old copy behind is untidy but harmless; the new key is authoritative.
    }
    return legacy;
  }

  return null;
}

function getStoredState(sessionId) {
  if (!sessionId) {
    return null;
  }
  const stored = readSessionValue(sessionId, STATE_KEY, getSessionMode(sessionId));
  if (stored != null) {
    return stored;
  }
  return migrateLegacyState(sessionId);
}

export function debug(str, val) {
  // console.log(str, val);
}

export function handleError(sessionId, error, failureCallback, functionName) {
  console.log(functionName + " Error : ", error);
  const message = getCaughtErrorPayload(error);
  if (failureCallback) {
    failureCallback(getErrorMessage(sessionId, message));
    return;
  }
  throw getErrorMessage(sessionId, message);
}

function getCaughtErrorPayload(error) {
  const responseData = error?.response?.data;
  return (
    responseData?.errorMessage ??
    responseData ??
    (typeof error === "string" ? error : error?.message ?? error)
  );
}

export function getErrorMessageFromCaughtError(sessionId, error) {
  return getErrorMessage(sessionId, getCaughtErrorPayload(error));
}
